import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Car, Plus, Pencil, Trash2, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", make: "", model: "", year: "", license_plate: "",
    vin: "", color: "", assigned_vendor_id: "", location_id: "", notes: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [vRes, vendorRes, locRes] = await Promise.all([
        API.get("/vehicles/"),
        API.get("/management/vendors?limit=200"),
        API.get("/management/locations"),
      ]);
      setVehicles(vRes.data.vehicles);
      setVendors(vendorRes.data.vendors);
      setLocations(locRes.data.locations);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ name: "", make: "", model: "", year: "", license_plate: "", vin: "", color: "", assigned_vendor_id: "", location_id: "", notes: "" });
    setEditId(null);
  };

  const openEdit = (v) => {
    setForm({
      name: v.name || "", make: v.make || "", model: v.model || "",
      year: v.year || "", license_plate: v.license_plate || "",
      vin: v.vin || "", color: v.color || "",
      assigned_vendor_id: v.assigned_vendor_id || "",
      location_id: v.location_id || "", notes: v.notes || ""
    });
    setEditId(v.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Vehicle name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, year: form.year ? parseInt(form.year) : null };
      if (!payload.assigned_vendor_id) payload.assigned_vendor_id = null;
      if (!payload.location_id) payload.location_id = null;
      if (editId) {
        await API.put(`/vehicles/${editId}`, payload);
        toast.success("Vehicle updated");
      } else {
        await API.post("/vehicles/", payload);
        toast.success("Vehicle added");
      }
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/vehicles/${id}`);
      toast.success("Vehicle deleted");
      fetchData();
    } catch (err) { toast.error("Failed to delete"); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-zinc-900">Vehicles</h1>
          <p className="text-sm text-zinc-500">Manage fleet vehicles and assignments</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-red-600 hover:bg-red-700 text-white text-xs"
          data-testid="add-vehicle-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Vehicle
        </Button>
      </div>

      {vehicles.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {vehicles.map(v => (
            <Card key={v.id} className="border border-zinc-200 card-hover" data-testid={`vehicle-${v.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center">
                      <Car className="w-4.5 h-4.5 text-zinc-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-zinc-900">{v.name}</p>
                      <p className="text-[10px] text-zinc-400">
                        {[v.make, v.model, v.year].filter(Boolean).join(" ")}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(v)}
                      className="text-[10px] h-6 px-2" data-testid={`edit-vehicle-${v.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(v.id)}
                      className="text-[10px] h-6 px-2 text-red-500" data-testid={`delete-vehicle-${v.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {v.license_plate && (
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-zinc-400 uppercase">Plate</span>
                      <span className="text-xs font-mono text-zinc-700">{v.license_plate}</span>
                    </div>
                  )}
                  {v.color && (
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-zinc-400 uppercase">Color</span>
                      <span className="text-xs text-zinc-700">{v.color}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-zinc-400 uppercase">Assigned</span>
                    <span className="text-xs text-zinc-700">{v.assigned_vendor_name || "Unassigned"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-zinc-400 uppercase">Location</span>
                    <span className="text-xs text-zinc-700">{v.location_name || "—"}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border border-dashed border-zinc-200">
          <CardContent className="p-8 text-center">
            <Car className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No vehicles added yet</p>
            <p className="text-xs text-zinc-400 mt-1">Add company vehicles to track expenses and assignments</p>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={() => { setShowForm(false); resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-zinc-500 font-bold">Vehicle Name *</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Service Van #1" className="w-full mt-1 px-3 py-2 border border-zinc-300 rounded-lg text-sm"
                data-testid="vehicle-name-input" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-zinc-500 font-bold">Make</label>
                <input value={form.make} onChange={e => setForm(p => ({ ...p, make: e.target.value }))}
                  placeholder="Ford" className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 font-bold">Model</label>
                <input value={form.model} onChange={e => setForm(p => ({ ...p, model: e.target.value }))}
                  placeholder="Transit" className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 font-bold">Year</label>
                <input type="number" value={form.year} onChange={e => setForm(p => ({ ...p, year: e.target.value }))}
                  placeholder="2024" className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-zinc-500 font-bold">License Plate</label>
                <input value={form.license_plate} onChange={e => setForm(p => ({ ...p, license_plate: e.target.value }))}
                  className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs font-mono" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 font-bold">Color</label>
                <input value={form.color} onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
                  className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs" />
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500 font-bold">VIN</label>
              <input value={form.vin} onChange={e => setForm(p => ({ ...p, vin: e.target.value }))}
                className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-zinc-500 font-bold">Assign Technician</label>
                <select value={form.assigned_vendor_id} onChange={e => setForm(p => ({ ...p, assigned_vendor_id: e.target.value }))}
                  className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs bg-white"
                  data-testid="vehicle-assign-tech">
                  <option value="">Unassigned</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.full_name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-zinc-500 font-bold">Location</label>
                <select value={form.location_id} onChange={e => setForm(p => ({ ...p, location_id: e.target.value }))}
                  className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs bg-white">
                  <option value="">No location</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white text-sm"
              data-testid="save-vehicle-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editId ? "Update Vehicle" : "Add Vehicle")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
